import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { ClientProvider } from './context/ClientContext';
import { fetchBranding } from './auth/brandingClient';
import { THEMES, buildThemeFromBranding } from './tokens/themes';
import { setActiveTheme } from './tokens/design';
import { ToastProvider } from './components/ui/Toast';
import App from './App';
import './index.css';

async function bootstrap() {
  // Platform-level routes must always show the domain-based (SSS) branding,
  // never a cached impersonated partner branding left over from a previous session.
  // One-time migration: clear stale pre-domain branding cache
  try { localStorage.removeItem('msp-branding-cache'); } catch (_) {}

  const pathname = window.location.pathname;
  const onPlatformRoute = pathname.startsWith('/platform');
  const onAuthRoute = pathname === '/login' || pathname === '/signin' || pathname.startsWith('/auth/sso/');
  if (onPlatformRoute || onAuthRoute) {
    try { localStorage.removeItem('msp-branding-cache-v1'); } catch (_) {}
  }

  // Try localStorage cache first so returning users get instant branding.
  // Auth routes must re-read live branding because SSO availability controls login actions.
  let branding;
  if (!onAuthRoute) {
    try {
      const raw = localStorage.getItem('msp-branding-cache-v1');
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < 8 * 60 * 60 * 1000) branding = data;
      }
    } catch (_) {}
  }

  if (!branding) {
    // Domain-based fetch works for all users - no auth required
    branding = await fetchBranding();
  }
  document.title = branding?.portalName || 'Security Portal';

  // Apply branding colors BEFORE React renders so first paint uses correct colors
  if (branding?.primaryColor || branding?.backgroundColor) {
    const brandingTheme = buildThemeFromBranding(branding);
    THEMES._branding = brandingTheme;
    setActiveTheme('_branding');
  }

  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <BrandingProvider branding={branding}>
        <ToastProvider>
          <ThemeProvider branding={branding}>
            <ClientProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </ClientProvider>
          </ThemeProvider>
        </ToastProvider>
      </BrandingProvider>
    </React.StrictMode>,
  );
}

bootstrap();
